'use client';
import { useEffect, useRef, useState } from 'react';
import { useConstantCallback, useHover } from '@THC/hooks';
import { sleep } from '@THC/storybook/helpers';
import { filterDataProps } from '@THC/utils/helpers';
import { toastEmitter } from './ToastEmitter';
export const useToaster = (props, { toasterRef }) => {
    const { position = 'top-right', ...rest } = props;
    const [toasts, setToasts] = useState([]);
    const [scheduledToasts, setScheduledToasts] = useState([]);
    const hasToasterHover = useRef(false);
    const scheduledToastsTimeoutRef = useRef();
    const sequencedToastCallback = useRef();
    const sequencedDismissedToastId = useRef();
    const getToasts = useConstantCallback(() => toasts);
    const toggleToastPause = ({ pause = false }) => {
        if (pause) {
            toastEmitter.emit('pause', null);
        }
        else {
            toastEmitter.emit('resume', null);
        }
    };
    const clearScheduledToastsTimeout = () => {
        if (scheduledToastsTimeoutRef.current) {
            clearTimeout(scheduledToastsTimeoutRef.current);
        }
    };
    const visibilityChangeHandler = useConstantCallback(() => {
        const isTabHidden = document.visibilityState === 'hidden';
        const isTabVisible = document.visibilityState === 'visible';
        if (isTabVisible) {
            clearScheduledToastsTimeout();
            scheduledToastsTimeoutRef.current = setTimeout(() => {
                setToasts((prevToasts) => [...prevToasts, ...scheduledToasts]);
                setScheduledToasts([]);
            }, 500);
        }
        toggleToastPause({ pause: isTabHidden });
    });
    useHover(toasterRef, ({ isHovered }) => {
        hasToasterHover.current = isHovered;
        toggleToastPause({ pause: isHovered });
    });
    useEffect(() => () => {
        clearScheduledToastsTimeout();
    }, []);
    useEffect(() => {
        document.addEventListener('visibilitychange', visibilityChangeHandler);
        return () => {
            document.removeEventListener('visibilitychange', visibilityChangeHandler);
        };
    }, [visibilityChangeHandler]);
    useEffect(() => {
        if (hasToasterHover.current) {
            toggleToastPause({ pause: true });
        }
    }, [toasts]);
    useEffect(() => {
        const removeHandler = (toastId) => {
            if (sequencedDismissedToastId.current === toastId) {
                sequencedDismissedToastId.current = '';
                sequencedToastCallback.current?.();
            }
        };
        toastEmitter.on('remove', removeHandler);
        return () => {
            toastEmitter.off('remove', removeHandler);
        };
    }, []);
    useEffect(() => {
        const addHandler = (nextToast) => {
            const isTabHidden = document.visibilityState === 'hidden';
            const nextToastForState = { ...nextToast, 'data-toaster-position': position };
            const updateStateHandler = (prevToasts) => [...prevToasts, nextToastForState];
            if (nextToast.sequenceKey) {
                const currentToasts = getToasts();
                const sameSequenceToast = currentToasts.find((toast) => toast.sequenceKey === nextToast.sequenceKey);
                if (sameSequenceToast) {
                    sequencedToastCallback.current = async () => {
                        await sleep(100);
                        setToasts((prevToasts) => [...prevToasts, nextToastForState]);
                    };
                    const toastId = sameSequenceToast.__internal?.toastId;
                    if (toastId) {
                        sequencedDismissedToastId.current = toastId;
                        toastEmitter.emit('dismiss', { toastId });
                    }
                    return;
                }
            }
            if (isTabHidden) {
                setScheduledToasts(updateStateHandler);
            }
            else {
                setToasts(updateStateHandler);
            }
        };
        const removeHandler = (toastId) => {
            setToasts((prevToasts) => prevToasts.filter((toast) => toast.__internal?.toastId !== toastId));
        };
        toastEmitter.on('add', addHandler);
        toastEmitter.on('remove', removeHandler);
        return () => {
            toastEmitter.off('add', addHandler);
            toastEmitter.off('remove', removeHandler);
        };
    }, [position]);
    return {
        toasterContainerProps: {
            'data-position': position,
            ...filterDataProps(rest),
        },
        toasterProps: {
            'aria-label': 'Notifications',
            role: 'region',
        },
        toasts,
    };
};
